import { formatLocalTimestamp } from "../../utils/dateTimeDisplay";
import { useEffect, useState } from "react";

interface OfflineIndicatorProps {
  isOnline: boolean;
}

function CloudOffIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4 shrink-0"
      fill="none"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3 3L21 21M7.2 7.2A5 5 0 0 1 16.3 9a4 4 0 1 1 1.2 7.8H9a4 4 0 0 1-1.8-7.6"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

export function OfflineIndicator({ isOnline }: OfflineIndicatorProps) {
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const refreshMetadata = () => {
      void import("../../pwa/indexedDB/syncMetadata")
        .then((module) => module.getSyncMetadata())
        .then((metadata) => {
          if (!cancelled) {
            setLastSyncedAt(metadata.lastSyncedAt);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setLastSyncedAt(null);
          }
        });
    };

    refreshMetadata();
    window.addEventListener("purchase-requisition-sync-complete", refreshMetadata);
    return () => {
      cancelled = true;
      window.removeEventListener("purchase-requisition-sync-complete", refreshMetadata);
    };
  }, []);

  if (isOnline) {
    return null;
  }

  return (
    <div
      className="flex min-h-9 items-center gap-2 border-b border-amber-200 bg-amber-100 px-4 py-2 text-sm text-amber-800"
      role="status"
      aria-live="polite"
    >
      <CloudOffIcon />
      <span>
        Offline -- requisition drafts and submissions are queued locally.
        {lastSyncedAt ? ` Last synced ${formatLocalTimestamp(new Date(lastSyncedAt))}.` : " No completed sync recorded on this device."}
      </span>
    </div>
  );
}
